import {
  CrsResolver,
  CoordinatesClient,
  createCoordinatesClient,
} from '@data-access/coordinates';
import {
  Client,
  createClient as createFetchClient,
} from '@hey-api/client-fetch';
import {
  Client as ScanDataClient,
  createClient as createScanDataClient,
} from '@data-access/geospatial/scan-data';
import { client as tcTcpsGeneratedClient } from '@data-access/connect/tc-tcps/generated';
import {
  Client as MobileMappingClient,
  createClient as createMobileMappingClient,
} from '@data-access/geospatial/mobile-mapping';
import {
  Client as ImagingClient,
  createClient as createImagingClient,
} from '@data-access/geospatial/imaging';
import { useQueryClient } from '@tanstack/solid-query';
import { useAuth } from '@trimble/trimble-id-solidjs';
import {
  Component,
  createContext,
  createResource,
  JSX,
  Show,
  useContext,
} from 'solid-js';
import { AlignmentApiService } from '@data-access/geospatial/alignments';
import { ImagingApiService } from '@data-access/geospatial/imaging';
import {
  MobileMappingApiService,
  SettingsApiService,
} from '@data-access/geospatial/mobile-mapping';
import { ScanDataApiService } from '@data-access/geospatial/scan-data';
import { TcTcpsApiService, TcRegion } from '@data-access/connect/tc-tcps';
import {
  createPsetServiceClient,
  TcPsetApiService,
} from '@data-access/connect/tc-pset';
import { attachAuth } from '@trimble/http-utils';
import { createResponseErrorLogger } from '@trimble/logging';
import { useLogger } from '@trimble/solidjs-utils';
import { TcUserApiService } from '@data-access/connect/tc-user';
import { TcEcomApiService } from '@data-access/connect/tc-ecom';

interface ApiServices {
  scanDataApi: ScanDataApiService;
  alignmentApi: AlignmentApiService;
  mobileMappingApi: MobileMappingApiService;
  settingsApi: SettingsApiService;
  imagingApi: ImagingApiService;
  tcTcpsApi: TcTcpsApiService;
  tcUserApi: TcUserApiService;
  ecomApi: TcEcomApiService;
  coordinatesClient: CoordinatesClient;
  crsResolver: CrsResolver;
  getTcpsRegionClient(region: TcRegion): Promise<Client>;
  getPsetApi(region: TcRegion): Promise<TcPsetApiService>;
  isProductionEnvironment: boolean;
}

const ApiServiceContext = createContext<ApiServices>();

export interface ApiServiceProviderProps {
  children?: JSX.Element;
  seuratDomain: string;
  alignmentsDomain: string;
  mobileMappingDomain: string;
  imagingDomain: string;
  tcTcpsDomain: string;
  tcUserDomain: string;
  ecomDomain: string;
  coordinatesDomain: string;
  clientName: string;
  clientVersion: string;
  isProductionEnvironment: boolean;
}

export const ApiServiceProvider: Component<ApiServiceProviderProps> = (
  props: ApiServiceProviderProps
) => {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const logger = useLogger();
  const apiErrorLogger = createResponseErrorLogger(
    logger.getLogger('ApiError')
  );

  const configureClient = (
    client: Client,
    accessToken: () => Promise<string>
  ): Client => {
    client.interceptors.response.use(apiErrorLogger);
    attachAuth(client, accessToken);
    return client;
  };

  const createClient = (
    baseUrl: string,
    accessToken: () => Promise<string>
  ): Client => {
    const client = createFetchClient({ baseUrl, global: false });
    return configureClient(client, accessToken);
  };

  const getTcpsRegionClient = async (region: TcRegion): Promise<Client> => {
    return createClient(region['tc-api'], getAccessToken);
  };

  const getPsetApi = async (region: TcRegion): Promise<TcPsetApiService> => {
    const client = createPsetServiceClient({ region });
    client.interceptors.response.use(apiErrorLogger);
    client.setConfig({
      auth: getAccessToken,
    });
    return new TcPsetApiService(client, queryClient);
  };

  const setupApiServices = async (): Promise<ApiServices> => {
    const alignmentsClient = createClient(
      props.alignmentsDomain,
      getAccessToken
    );
    const mobileMappingClient = configureMobileMappingClient(
      props.mobileMappingDomain,
      getAccessToken
    );
    const imagingClient = configureImagingClient(
      props.imagingDomain,
      getAccessToken
    );
    const scanDataClient = configureScanDataClient(
      props.seuratDomain,
      getAccessToken,
      apiErrorLogger
    );

    const tcTcpsClient = createClient(props.tcTcpsDomain, getAccessToken);
    configureClient(tcTcpsGeneratedClient as Client, getAccessToken);

    tcTcpsGeneratedClient.setConfig({ baseUrl: props.tcTcpsDomain });

    const tcUserClient = createClient(props.tcUserDomain, getAccessToken);
    const ecomClient = createClient(props.ecomDomain, getAccessToken);

    const coordinatesClient = createCoordinatesClient({
      baseUrl: props.coordinatesDomain,
    });
    coordinatesClient.interceptors.request.use(async (config) => {
      const accessToken = await getAccessToken();
      config.headers.set('Authorization', `Bearer ${accessToken}`);
      return config;
    });
    coordinatesClient.interceptors.response.use(apiErrorLogger);

    const scanDataApi = new ScanDataApiService(
      scanDataClient,
      props.clientName,
      props.clientVersion,
      queryClient
    );
    const alignmentApi = new AlignmentApiService(alignmentsClient);
    const mobileMappingApi = new MobileMappingApiService(
      mobileMappingClient,
      queryClient
    );
    const settingsApi = new SettingsApiService(mobileMappingClient);
    const imagingApi = new ImagingApiService(
      imagingClient,
      props.clientName,
      props.clientVersion,
      queryClient
    );
    const tcTcpsApi = new TcTcpsApiService(tcTcpsClient, queryClient);
    const tcUserApi = new TcUserApiService(tcUserClient);
    const ecomApi = new TcEcomApiService(ecomClient);

    const crsResolver = new CrsResolver(
      coordinatesClient,
      logger.getLogger('CrsResolver')
    );

    return {
      scanDataApi,
      alignmentApi,
      mobileMappingApi,
      settingsApi,
      imagingApi,
      tcTcpsApi,
      tcUserApi,
      ecomApi,
      coordinatesClient,
      crsResolver,
      getTcpsRegionClient,
      getPsetApi,
      isProductionEnvironment: props.isProductionEnvironment,
    };
  };

  const [clients] = createResource(setupApiServices);

  function getAccessToken(): Promise<string> {
    return auth().getAccessTokenSilently();
  }

  return (
    <Show
      when={auth().isAuthenticated}
      fallback={<div>Could not authenticate!</div>}
    >
      <Show when={clients()}>
        <ApiServiceContext.Provider value={clients()}>
          {props.children}
        </ApiServiceContext.Provider>
      </Show>
    </Show>
  );
};

export const useApiServices = (): ApiServices => {
  const context = useContext(ApiServiceContext);
  if (context === undefined) {
    throw new Error('useApiServices must be used within a ApiServiceProvider');
  }

  return context;
};

// TODO: Consolidate this when all the other apis are re-generated
const configureMobileMappingClient = (
  baseUrl: string,
  accessToken: () => Promise<string>
): MobileMappingClient => {
  const client = createMobileMappingClient({ baseUrl });

  client.interceptors.request.use(async (config: Request) => {
    const token = await accessToken();
    config.headers.set('Authorization', `Bearer ${token}`);
    return config;
  });

  return client;
};

// TODO:  Consolidate this when all the other apis are re-generated
const configureImagingClient = (
  baseUrl: string,
  accessToken: () => Promise<string>
): ImagingClient => {
  const client = createImagingClient({ baseUrl });

  client.interceptors.request.use(async (config: Request) => {
    const token = await accessToken();
    config.headers.set('Authorization', `Bearer ${token}`);
    return config;
  });

  return client;
};

const configureScanDataClient = (
  baseUrl: string,
  accessToken: () => Promise<string>,
  onResponse: (response: Response, request: Request) => Promise<Response>
): ScanDataClient => {
  const client = createScanDataClient({ baseUrl });

  client.interceptors.response.use((response, request, _options) =>
    onResponse(response, request)
  );
  client.interceptors.request.use(async (request, _options) => {
    const token = await accessToken();
    request.headers.set('Authorization', `Bearer ${token}`);
    return request;
  });

  return client;
};
